<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';

const SIZE_ORDER = [
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
];

function sizeIndex(beden) {
  const i = SIZE_ORDER.indexOf(String(beden || '').toUpperCase());
  return i === -1 ? 999 : i;
}

const route = useRoute();
const router = useRouter();

const product = ref(null);
const loading = ref(true);
const error = ref('');
const selectedColor = ref('');
const selectedImage = ref('');

const colorGroups = computed(() => product.value?.colorGroups || []);

const selectedGroup = computed(
  () =>
    colorGroups.value.find(g => g.colorCode === selectedColor.value) ??
    colorGroups.value[0] ??
    null
);

const currentImages = computed(() => {
  const imgs = selectedGroup.value?.images || [];
  return imgs.length ? imgs : product.value?.images || [];
});

const sizesForColor = computed(() =>
  [...(selectedGroup.value?.variants || [])].sort(
    (a, b) =>
      sizeIndex(a.beden) - sizeIndex(b.beden) ||
      String(a.beden).localeCompare(String(b.beden))
  )
);

const colorTotalStock = computed(() =>
  sizesForColor.value.reduce((sum, v) => sum + (v.stock || 0), 0)
);

function selectColor(group) {
  selectedColor.value = group.colorCode;
  selectedImage.value = (group.images && group.images[0]) || '';
}

function priceLabel() {
  const min = product.value?.minSalePrice;
  const max = product.value?.maxSalePrice;
  if (!min) return null;
  return min === max
    ? `${min.toFixed(2)} ₺`
    : `${min.toFixed(2)} – ${max.toFixed(2)} ₺`;
}

function goBack() {
  router.push({
    name: 'b2b_index',
    params: { accountId: route.params.accountId },
  });
}

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const data = await fetchMarketplaceJson(
      route.params.accountId,
      `b2b_products/${route.params.productId}`
    );
    product.value = data.product;
    const first = colorGroups.value[0];
    if (first) {
      selectedColor.value = first.colorCode;
      selectedImage.value = (first.images && first.images[0]) || '';
    }
  } catch (e) {
    error.value = 'Ürün yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

const importing = ref(false);
const importMsg = ref('');
async function importToCenter() {
  importing.value = true;
  importMsg.value = '';
  try {
    const res = await postMarketplaceJsonBody(
      route.params.accountId,
      'product_center/import',
      { ticimaxId: route.params.productId }
    );
    importMsg.value = res.imported
      ? 'Ürün Merkezine aktarıldı ✓'
      : 'Zaten merkezde';
  } catch (e) {
    importMsg.value = 'Hata: ' + e.message;
  } finally {
    importing.value = false;
  }
}

onMounted(loadData);
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        @click="goBack"
      >
        <span class="i-lucide-arrow-left size-3.5" />
        Katalog
      </button>
      <div class="min-w-0">
        <h1
          class="text-base font-semibold text-slate-900 dark:text-slate-100 truncate"
        >
          {{ product?.title || 'Ürün' }}
        </h1>
        <p class="text-xs text-slate-400">Adrom Toptan · Ticimax</p>
      </div>
      <button
        class="ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
        :disabled="importing"
        @click="importToCenter"
      >
        <span
          :class="
            importing ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-database'
          "
          class="size-3.5"
        />
        {{ importMsg || (importing ? 'Aktarılıyor…' : 'Ürün Merkezine Aktar') }}
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-5">
      <!-- Loading -->
      <div
        v-if="loading"
        class="flex items-center justify-center h-48 text-slate-400 gap-2"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin" />
        <span class="text-sm">Ürün yükleniyor…</span>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
      >
        <span class="i-lucide-alert-circle size-5 shrink-0" />{{ error }}
      </div>

      <!-- Detail -->
      <div
        v-else-if="product"
        class="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl"
      >
        <!-- Gallery -->
        <div>
          <div
            class="aspect-[2/3] rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 shadow-sm"
          >
            <img
              v-if="selectedImage"
              :src="selectedImage"
              :alt="product.title"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-slate-300"
            >
              <span class="i-lucide-image size-12" />
            </div>
          </div>
          <div
            v-if="currentImages.length > 1"
            class="flex flex-wrap gap-2 mt-3"
          >
            <button
              v-for="(img, idx) in currentImages"
              :key="idx"
              class="size-14 rounded-lg overflow-hidden border-2 transition"
              :class="
                selectedImage === img
                  ? 'border-woot-500'
                  : 'border-transparent hover:border-slate-300'
              "
              @click="selectedImage = img"
            >
              <img :src="img" class="w-full h-full object-cover" />
            </button>
          </div>
        </div>

        <!-- Info -->
        <div>
          <div class="flex items-center gap-2 flex-wrap">
            <span
              class="px-2 py-1 rounded-lg text-xs font-bold"
              :class="
                product.totalStock > 0
                  ? 'bg-green-500/90 text-white'
                  : 'bg-red-500/90 text-white'
              "
            >
              Toplam stok: {{ product.totalStock }}
            </span>
            <span
              v-if="priceLabel()"
              class="px-2 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            >
              {{ priceLabel() }}
            </span>
          </div>

          <!-- Maliyet -->
          <div
            v-if="product.costUsd || product.costTry"
            class="mt-4 flex flex-wrap gap-3"
          >
            <div
              v-if="product.costUsd"
              class="flex-1 min-w-[140px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3"
            >
              <p class="text-xs text-slate-400">Dolar Maliyet (adet)</p>
              <p class="text-lg font-bold text-slate-900 dark:text-slate-100">
                ${{ product.costUsd.toFixed(2) }}
              </p>
            </div>
            <div
              v-if="product.costTry"
              class="flex-1 min-w-[140px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3"
            >
              <p class="text-xs text-slate-400">TL Maliyet (adet, KDV dahil)</p>
              <p class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ product.costTry.toFixed(2) }} ₺
              </p>
            </div>
          </div>

          <!-- Colors -->
          <div class="mt-5">
            <p
              class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide"
            >
              Renk ({{ colorGroups.length }})
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="group in colorGroups"
                :key="group.colorCode"
                class="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition"
                :class="
                  group.colorCode === selectedColor
                    ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 font-semibold'
                    : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                "
                @click="selectColor(group)"
              >
                <span
                  v-if="group.images && group.images[0]"
                  class="size-6 rounded overflow-hidden"
                >
                  <img
                    :src="group.images[0]"
                    class="w-full h-full object-cover"
                  />
                </span>
                {{ group.colorName }}
              </button>
            </div>
          </div>

          <!-- Size / stock table -->
          <div class="mt-6">
            <div class="flex items-center justify-between mb-2">
              <p
                class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide"
              >
                Beden / Stok
              </p>
              <span class="text-xs text-slate-400">
                Bu renkte toplam: {{ colorTotalStock }} adet
              </span>
            </div>
            <div
              class="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <table class="w-full text-sm">
                <thead
                  class="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                >
                  <tr>
                    <th class="text-left font-medium px-3 py-2">Beden</th>
                    <th class="text-right font-medium px-3 py-2">Stok</th>
                    <th class="text-right font-medium px-3 py-2">Fiyat</th>
                    <th
                      class="text-left font-medium px-3 py-2 hidden sm:table-cell"
                    >
                      Stok Kodu
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="v in sizesForColor"
                    :key="v.sku || v.beden"
                    class="border-t border-slate-100 dark:border-slate-800"
                  >
                    <td
                      class="px-3 py-2 font-semibold text-slate-800 dark:text-slate-200"
                    >
                      {{ v.beden || '—' }}
                    </td>
                    <td
                      class="px-3 py-2 text-right font-bold"
                      :class="
                        v.stock > 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-500'
                      "
                    >
                      {{ v.stock }}
                    </td>
                    <td
                      class="px-3 py-2 text-right text-slate-600 dark:text-slate-300"
                    >
                      {{ v.price ? v.price.toFixed(2) + ' ₺' : '—' }}
                    </td>
                    <td
                      class="px-3 py-2 text-xs text-slate-400 hidden sm:table-cell truncate"
                    >
                      {{ v.sku || '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Aciklama -->
          <div v-if="product.description" class="mt-6">
            <p
              class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide"
            >
              Ürün Açıklaması
            </p>
            <p
              class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line"
            >
              {{ product.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
